var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMemo } from 'react';
import * as jsonStableStringify from 'json-stable-stringify';
import { useSubscription } from 'use-subscription';
import { deprecate } from '../util';
const increase = (v = 0) => (v + 1);
const decrease = (v = 0) => (v - 1 > 0 ? v - 1 : 0);
const toPromise = ({ asyncFunction, params }) => __awaiter(void 0, void 0, void 0, function* () {
    if (typeof asyncFunction === 'function') {
        return asyncFunction(params);
    }
    // promise
    return asyncFunction;
});
const formatLoading = (loading) => {
    // treat undefined as true
    if (loading === undefined) {
        return true;
    }
    return loading > 0;
};
const formatError = (error) => {
    return typeof error === 'string' ? new Error(error) : error;
};
const getSetResult = (resultOrFunc, snapshot) => {
    if (typeof resultOrFunc === 'function') {
        return resultOrFunc(snapshot);
    }
    return resultOrFunc;
};
function createMappedRegion(initialValue, option) {
    var _a;
    const strategy = (_a = option === null || option === void 0 ? void 0 : option.strategy) !== null && _a !== void 0 ? _a : 'acceptLatest';
    const private_stateRef = {
        current: {},
    };
    const private_store_ensure = (key) => {
        if (!private_stateRef.current[key]) {
            private_stateRef.current[key] = {};
        }
    };
    const private_store_emit = (key) => {
        const props = private_stateRef.current[key];
        if (!props) {
            return;
        }
        const { listeners } = props;
        if (!listeners) {
            return;
        }
        listeners.forEach(listener => listener());
    };
    // only used for test
    const private_store_getState = () => {
        return private_stateRef.current;
    };
    // only used for test
    const private_store_setState = (value) => {
        private_stateRef.current = value;
    };
    const private_store_getAttribute = (key, attribute) => {
        const props = private_stateRef.current[key];
        if (!props) {
            return undefined;
        }
        return props[attribute];
    };
    const private_store_load = (key, promise) => {
        private_store_ensure(key);
        // since it is ensured
        const props = private_stateRef.current[key];
        props.promise = promise;
        props.pendingMutex = increase(props.pendingMutex);
        private_store_emit(key);
    };
    const private_store_loadEnd = (key) => {
        private_store_ensure(key);
        // since it is ensured
        const props = private_stateRef.current[key];
        props.pendingMutex = decrease(props.pendingMutex);
        private_store_emit(key);
    };
    const private_store_set = (key, value) => {
        private_store_ensure(key);
        // since it is ensured
        const props = private_stateRef.current[key];
        const snapshot = props.value;
        const formatValue = typeof value === 'function' ? value(snapshot) : value;
        props.pendingMutex = decrease(props.pendingMutex);
        props.value = formatValue;
        props.error = undefined; // reset error
        private_store_emit(key);
    };
    const private_store_setError = (key, error) => {
        private_store_ensure(key);
        // since it is ensured
        const props = private_stateRef.current[key];
        props.pendingMutex = decrease(props.pendingMutex);
        props.error = error;
        if (error) {
            console.error(error);
        }
        private_store_emit(key);
    };
    const private_store_reset = (key) => {
        private_stateRef.current[key] = { listeners: private_stateRef.current[key].listeners };
        private_store_emit(key);
    };
    const private_store_resetAll = () => {
        Object.keys(private_stateRef.current).forEach((key) => {
            private_stateRef.current[key] = { listeners: private_stateRef.current[key].listeners };
            private_store_emit(key);
        });
    };
    // TODO add a regress test to prevent shadow variable usage of listeners
    const private_store_subscribe = (key, listener) => {
        private_store_ensure(key);
        // since it is ensured
        const props = private_stateRef.current[key];
        if (!props.listeners) {
            props.listeners = [];
        }
        if (typeof listener === 'function') {
            props.listeners.push(listener);
        }
        else {
            console.warn(`listener should be function, but received ${listener}`);
        }
        return () => {
            private_store_ensure(key);
            // since it is ensured
            const props = private_stateRef.current[key];
            if (!props.listeners) {
                props.listeners = [];
            }
            props.listeners.splice(props.listeners.indexOf(listener), 1);
        };
    };
    /* -------- */
    const getKeyString = (key) => {
        if (typeof key === 'string') {
            return key;
        }
        return jsonStableStringify(key);
    };
    const getValueOrInitialValue = (value) => {
        if (value !== undefined) {
            return value;
        }
        return initialValue;
    };
    // ---- APIs ----
    const set = (key, resultOrFunc) => {
        const keyString = getKeyString(key);
        // Maybe we can use getValue here
        const maybeSnapshot = private_store_getAttribute(keyString, 'value');
        const snapshot = getValueOrInitialValue(maybeSnapshot);
        const result = getSetResult(resultOrFunc, snapshot);
        private_store_set(keyString, result);
        return result;
    };
    const reset = (key) => {
        if (key === undefined) {
            deprecate('reset should be called with key, use resetAll to reset all keys.');
            private_store_resetAll();
        }
        const keyString = getKeyString(key);
        private_store_reset(keyString);
    };
    const resetAll = private_store_resetAll;
    // const emit: Result['emit'] = (key: K) => {
    //   const keyString = getKeyString(key);
    //   private_store_emit(keyString);
    // };
    //
    // const emitAll: Result['emitAll'] = private_store_emitAll;
    const loadBy = (key, asyncFunction, reducer) => {
        const loadByReturnFunction = (params) => __awaiter(this, void 0, void 0, function* () {
            const loadKey = typeof key === 'function' ? key(params) : key;
            const keyString = getKeyString(loadKey);
            const promise = toPromise({ asyncFunction, params });
            private_store_load(keyString, promise);
            /**
         * note
         * 1. always get value after await, so it is the current one
         * 2. ensure if initialValue is gaven, every branch should return initialValueOfKey as T[K]
         */
            try {
                const result = yield promise;
                const currentPromise = private_store_getAttribute(keyString, 'promise');
                const snapshot = private_store_getAttribute(keyString, 'value');
                const formattedResult = typeof reducer === 'function'
                    ? reducer(getValueOrInitialValue(snapshot), result, params)
                    : result;
                if (strategy === 'acceptLatest' && promise !== currentPromise) {
                    // decrease loading & return snapshot
                    private_store_loadEnd(keyString);
                    return getValueOrInitialValue(snapshot);
                }
                private_store_set(keyString, formattedResult);
                return getValueOrInitialValue(formattedResult);
            }
            catch (error) {
                const currentPromise = private_store_getAttribute(keyString, 'promise');
                const snapshot = private_store_getAttribute(keyString, 'value');
                if (strategy === 'acceptLatest' && promise !== currentPromise) {
                    // decrease loading & return snapshot
                    private_store_loadEnd(keyString);
                    return getValueOrInitialValue(snapshot);
                }
                private_store_setError(keyString, error);
                return getValueOrInitialValue(snapshot);
            }
        });
        return loadByReturnFunction;
    };
    const load = (key, promise) => __awaiter(this, void 0, void 0, function* () {
        if (promise instanceof Promise || typeof promise === 'function') {
            return loadBy(key, promise)();
        }
        console.warn('set result directly');
        return set(key, promise);
    });
    const getValue = key => {
        const keyString = getKeyString(key);
        const value = private_store_getAttribute(keyString, 'value');
        return getValueOrInitialValue(value);
    };
    const getLoading = key => {
        const keyString = getKeyString(key);
        return formatLoading(private_store_getAttribute(keyString, 'pendingMutex'));
    };
    const getError = key => {
        const keyString = getKeyString(key);
        return formatError(private_store_getAttribute(keyString, 'error'));
    };
    const createHooks = (getFn) => {
        return (key) => {
            const subscription = useMemo(() => ({
                getCurrentValue: () => getFn(key),
                subscribe: (listener) => private_store_subscribe(getKeyString(key), listener),
            }), 
            // shallow-equal
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [getFn, getKeyString(key)]);
            return useSubscription(subscription);
        };
    };
    const useValue = (key, selector) => {
        // keep logic as createHook is
        const getFn = getValue;
        const subscription = useMemo(() => ({
            getCurrentValue: () => (selector ? selector(getFn(key)) : getFn(key)),
            subscribe: (listener) => private_store_subscribe(getKeyString(key), listener),
        }), 
        // shallow-equal
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [getFn, selector, getKeyString(key)]);
        return useSubscription(subscription);
    };
    const useLoading = createHooks(getLoading);
    const useError = createHooks(getError);
    return {
        private_getState_just_for_test: private_store_getState,
        private_setState_just_for_test: private_store_setState,
        set,
        reset,
        resetAll,
        // emit,
        // emitAll,
        load,
        loadBy,
        getValue,
        getLoading,
        getError,
        useValue,
        useLoading,
        useError,
    };
}
export default createMappedRegion;
